// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { groupBy } from '@shared/ui-kit';
import { useMemo } from 'react';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';

export interface UseAccountsPositionsProps {
  clientId?: number | null;
  ibans: string[];
  skip?: boolean;
  institutionId: string;
  currency: AccountCurrency;
}

export const useAccountsPositions = ({
  clientId,
  ibans,
  skip,
  institutionId,
  currency,
}: UseAccountsPositionsProps) => {
  const accountsPositionsQuery = advisoryApi.useGetAdvisoryAccountsPositionsQuery(
    {
      client_id: clientId,
      currency,
      institution_id: institutionId,
    },
    { skip: skip },
  );

  const accountsPositions = useMemo(() => {
    const data = accountsPositionsQuery.data ?? [];

    return data.filter(account => ibans.includes(account.iban));
  }, [accountsPositionsQuery.data, ibans]);

  const investment = useMemo(() => {
    return accountsPositions.reduce(
      (acc, account) =>
        acc +
        account.holdings.reduce((acc, holding) => acc + holding.investment, 0),
      0,
    );
  }, [accountsPositions]);

  const holdings = useMemo(() => {
    return accountsPositions.flatMap(account => {
      return account.holdings.map(holding => ({
        ...holding,
        weight: holding.investment / investment,
        institution_id: account.institution_id,
        institution_name: account.institution_name,
        iban: account.iban,
        subRows: [],
      }));
    }) as AccountsHoldingsData[];
  }, [accountsPositions, investment]);

  const assetClassHoldings = useMemo(() => {
    if (holdings.length === 0) return [];

    const assetClasses = groupBy(holdings, 'asset_class');

    return [
      {
        asset_class: '',
        name: 'total',
        asset_type: '',
        currency: '',
        investment: investment,
        price: -1,
        institution_id: '',
        institution_name: '',
        quantity: -1,
        weight: 1,
        iban: '',
        subRows: [],
      },
      ...Array.from(assetClasses.entries()).map(([assetClass, holdings]) => {
        const investment = holdings.reduce(
          (acc, holding) => acc + holding.investment,
          0,
        );

        const weight = holdings.reduce((acc, holding) => acc + holding.weight, 0);

        return {
          institution_id: '',
          institution_name: '',
          asset_type: assetClass,
          asset_class: assetClass,
          currency: '',
          subRows: holdings,
          name: assetClass,
          price: -1,
          quantity: -1,
          investment: investment,
          weight: weight,
          iban: '',
        } as AccountsHoldingsData;
      }),
    ];
  }, [holdings, investment]);

  const assetTypeHoldings = useMemo(() => {
    if (holdings.length === 0) return [];

    const assetTypes = groupBy(holdings, 'asset_type');

    return [
      {
        asset_class: '',
        name: 'total',
        asset_type: '',
        currency: '',
        investment: investment,
        price: -1,
        institution_id: '',
        institution_name: '',
        quantity: -1,
        weight: 1,
        iban: '',
        subRows: [],
      } as AccountsHoldingsData,
      ...Array.from(assetTypes.entries()).map(([assetType, holdings]) => {
        const investment = holdings.reduce(
          (acc, holding) => acc + holding.investment,
          0,
        );

        const weight = holdings.reduce((acc, holding) => acc + holding.weight, 0);

        return {
          institution_id: '',
          institution_name: '',
          asset_type: assetType,
          asset_class: assetType,
          currency: '',
          subRows: holdings,
          name: assetType,
          price: -1,
          quantity: -1,
          investment: investment,
          weight: weight,
          iban: '',
        } as AccountsHoldingsData;
      }),
    ];
  }, [holdings, investment]);

  return {
    holdings,
    assetClassHoldings,
    assetTypeHoldings,
    investment,
    accountsPositionsQuery,
  };
};
